import {Application} from "../Application";
import {Ease} from "../core/Animatable";
import {texture} from "../core/AssetHelpers";
import {BaseButton, HoverableButton} from "../core/Button";
import {Container} from "../core/Container";
import {Callback, Mix} from "../core/Reflection";
import {Poly} from "../core/Shape";
import {Sprite} from "../core/Sprite";
import {Assets} from "../data/Assets";
import {Location} from "../data/Location";
import {AudioChannel} from "../managers/AudioChannel";
import {PulsatingButton} from "./Button";
import {Focusable} from "./Focusable";
import {Postcard} from "./Postcard";
import {TransformBackup} from "./TransformBackup";

export class Envelope extends Mix(BaseButton, HoverableButton(), Focusable, TransformBackup, PulsatingButton) {

    public readonly envelopeOpen = this.visuals.addChild(new Sprite());
    public readonly envelopeClosed = this.visuals.addChild(new Sprite());
    public readonly postcardMask: number[] = [];
    public readonly postcardBounds = {x: 0, y: 0, width: 0, height: 0};

    public onLayout() {
        this.saveTransform();
    }

    public setLocation(location: Location) {

        this.envelopeOpen.texture = texture(`envelope-${location}-open`)!;
        this.envelopeClosed.texture = texture(`envelope-${location}-closed`)!;

        this.envelopeOpen.scale.x = this.envelopeClosed.scale.x;
        this.envelopeOpen.scale.y = this.envelopeClosed.scale.y;
        this.envelopeOpen.y = this.envelopeClosed.y;
        this.envelopeOpen.anchor.x = this.envelopeClosed.anchor.x;
        this.envelopeOpen.anchor.y = this.envelopeClosed.anchor.y;
        this.envelopeOpen.visible = false;
    }

    public hide(duration: number, cb?: Callback) {
        this.moveYTo(Application.instance.resizer.getSize().y, duration, Ease.sineIn, cb);
        this.stopPulse();
    }

    public show(duration: number, cb?: Callback) {
        this.moveYTo(this.getTransformBackup().y, duration, Ease.backOut, () => {
            this.pulse(1.1, 1.5, true);
            if (cb) {
                cb();
            }
        });
    }

    public open(postcard: Postcard, cb?: Callback) {
        this.envelopeOpen.visible = true;
        this.envelopeClosed.visible = false;
        const postcardParent = postcard.parent;
        const postcardTransform = postcard.getTransformBackup(true);
        postcard.changeParent(this.visuals);
        postcard.mask = this.visuals.addChild(new Poly(this.postcardMask));
        const w = postcard.background.width;
        const h = postcard.background.height;
        const s = Math.min(this.postcardBounds.width / w, this.postcardBounds.height / h);
        postcard.scale.set(s);
        postcard.x = this.postcardBounds.x + this.postcardBounds.width / 2;
        postcard.y = this.postcardBounds.y + s * h / 2;
        postcard.moveYTo(this.postcardBounds.y - this.postcardBounds.height / 2, 0.8, Ease.sineOut, {
            delay: 0.8,
            onStart: () => {
                AudioChannel.SFX.play(Assets.audio.postcardSlide.assetName);
            },
            onComplete: () => {
                postcard.mask?.destroy();
                postcard.mask = null;
                postcard.changeParent(postcardParent);
                postcard.tweenTo(0.6, Ease.sineInOut, {
                    ...postcardTransform,
                    onComplete: cb,
                });
                this.moveYTo(Application.instance.size.y + this.height, 0.4, Ease.sineIn);
            },
        });
        this.delayedCall(0.05, () => AudioChannel.SFX.play(Assets.audio.envelopeOpen.assetName));
    }
}

export class EnvelopeTransform extends Container {
    public readonly envelopeClosed = {};
}
